import React, { useRef, useState, useEffect, CSSProperties } from "react";
import { createPopper, Instance, Placement } from "@popperjs/core";
import ArrowIcon from "./ArrowIcon";
import "../../resources/scss/tooltip/tooltip.scss";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: Placement;
  trigger?: "hover" | "click";
  containerStyles?: CSSProperties;
  tooltipStyles?: CSSProperties;
  arrow?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  placement = "top",
  trigger = "hover",
  containerStyles = {},
  tooltipStyles = {},
  arrow = true,
}) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const popperInstance = useRef<Instance | null>(null);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    if (containerRef.current && tooltipRef.current && arrowRef.current) {
      popperInstance.current = createPopper(
        containerRef.current,
        tooltipRef.current,
        {
          placement,
          modifiers: [
            { name: "offset", options: { offset: [0, 8] } },
            { name: "arrow", options: { element: arrowRef.current } },
          ],
        }
      );
    }

    return () => {
      popperInstance.current?.destroy();
      popperInstance.current = null;
    };
  }, [placement]);

  useEffect(() => {
    popperInstance.current?.setOptions((options) => ({
      ...options,
      modifiers: [
        ...(options.modifiers || []),
        { name: "eventListeners", enabled: visible },
      ],
    }));

    if (visible) {
      popperInstance.current?.update();
    }
  }, [visible]);

  useEffect(() => {
    if (trigger !== "click" || !visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current &&
        tooltipRef.current &&
        !containerRef.current.contains(target) &&
        !tooltipRef.current.contains(target)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, trigger]);

  const show = () => {
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    setVisible(true);
  };

  const hide = () => {
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
    }, 100);
  };

  const toggle = () => {
    if (visible) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  };

  const containerProps =
    trigger === "hover"
      ? {
          onMouseEnter: show,
          onMouseLeave: hide,
          onFocus: show,
          onBlur: hide,
        }
      : {
          onClick: toggle,
        };

  const tooltipProps =
    trigger === "hover"
      ? {
          onMouseEnter: show,
          onMouseLeave: hide,
        }
      : {};

  if (!content) {
    return (
      <div
        ref={containerRef}
        {...containerProps}
        style={{ display: "inline-block", ...containerStyles }}
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        {...containerProps}
        style={{ display: "inline-block", ...containerStyles }}
      >
        {children}
      </div>
      <div
        ref={tooltipRef}
        role="tooltip"
        className={`cm_tooltip ${visible ? "cm_show" : ""}`}
        {...tooltipProps}
        style={{ ...tooltipStyles }}
      >
        <div className="cm_tooltip-content">{content}</div>
        {arrow && (
          <div ref={arrowRef} data-popper-arrow className="cm_custom-arrow">
            <ArrowIcon />
          </div>
        )}
      </div>
    </>
  );
};

export default Tooltip;
